import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LockOpen } from "lucide-react";
import { toast } from "sonner";

// Settings-page card for POST /stock/release-all (admin): unblock the stock
// of every open Sales Order and unpack every open Packing Slip that is not
// yet on a Delivery Challan. Preview first (dry run), then release.
const codes = (list) => (list.length > 12 ? `${list.slice(0, 12).join(", ")} +${list.length - 12} more` : list.join(", "));

export default function ReleaseAllStockSettings() {
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (dryRun) => {
    if (!dryRun && !reason.trim()) return toast.error("Please enter a reason");
    setBusy(true);
    try {
      const { data } = await api.post("/stock/release-all", { dry_run: dryRun, reason: reason.trim() });
      if (dryRun) {
        setPreview(data);
      } else {
        toast.success(`Unblocked ${data.orders_unblocked} order(s), unpacked ${data.slips_unpacked} Packing Slip(s)`);
        setPreview(null);
        setReason("");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not release stock");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="release-all-stock">
      <div className="flex items-center gap-2 mb-1">
        <LockOpen className="w-5 h-5 text-orange-600" />
        <h3 className="font-semibold text-slate-900">Release all blocked & packed stock</h3>
      </div>
      <p className="text-sm text-slate-500">
        Unblocks the stock of every open Sales Order and unpacks every open Packing Slip (packed qty back to 0, slip back to
        draft). Slips already on a Delivery Challan are not touched. Preview first — nothing changes until you release.
      </p>
      <div className="mt-4 flex gap-2">
        <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="release-all-preview">Preview</Button>
      </div>
      {preview && (
        <div className="mt-4 space-y-3">
          <div className="text-sm bg-orange-50 border border-orange-200 rounded-md px-3 py-2 text-orange-900" data-testid="release-all-summary">
            <div><strong>{preview.orders_unblocked}</strong> order(s) to unblock{preview.order_codes.length > 0 && <>: <span className="mono text-xs">{codes(preview.order_codes)}</span></>}</div>
            <div className="mt-1"><strong>{preview.slips_unpacked}</strong> Packing Slip(s) to unpack ({preview.packed_qty_released} qty){preview.slip_codes.length > 0 && <>: <span className="mono text-xs">{codes(preview.slip_codes)}</span></>}</div>
          </div>
          {(preview.orders_unblocked > 0 || preview.slips_unpacked > 0) && (
            <>
              <div>
                <Label>Reason *</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="release-all-reason" />
              </div>
              <Button onClick={() => run(false)} disabled={busy} className="bg-orange-600 hover:bg-orange-700" data-testid="release-all-confirm">
                {busy ? "Releasing…" : "Unblock & unpack all"}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
